/**
 * Mailbox list item from GET /mailboxes (FR4 canonical dimensions + legacy fields).
 * @see docs/openapi.json — MailboxStatusItemSchema
 */

export type MailboxConnectionState = 'connected' | 'revoked' | 'error';

export type MailboxSyncHealth = 'idle' | 'syncing' | 'completed' | 'failed';

/** Stable server-side classification; prefer over parsing sync_error text. */
export type MailboxErrorClass =
    | 'oauth_expired'
    | 'sync_failed'
    | 'connection_revoked'
    | 'connection_error'
    | 'provider_sync_unavailable';

/** Canonical sync stages (Story 9.1); UI labels deferred to 9.3. */
export type SyncStage =
    | 'requested'
    | 'authenticating'
    | 'metadata_fetch'
    | 'email_insert'
    | 'workitem_migration'
    | 'enrichment'
    | 'complete'
    | 'failed';

export type SyncStageOutcome = 'entered' | 'exited' | 'skipped' | 'failed';

export interface SyncJobStageEvent {
    id: string;
    stage: SyncStage;
    entered_at: string;
    attempt?: number;
    outcome?: SyncStageOutcome;
    error_class?: string | null;
    details?: Record<string, unknown>;
}

export interface SyncJob {
    id: string;
    mailbox_connection_id: number;
    current_stage: SyncStage;
    requested_at: string;
    started_at?: string | null;
    completed_at?: string | null;
    error_class?: string | null;
    error_message?: string | null;
    trigger_source?: string;
    metrics?: Record<string, unknown>;
    stage_events?: SyncJobStageEvent[];
}

export type MailboxSyncTriggerStatus = 'sync_started' | 'already_in_progress';

export interface MailboxSyncTriggerRequest {
    start_date?: string | null;
    end_date?: string | null;
    query?: string | null;
}

export interface MailboxSyncTriggerResponse {
    status: MailboxSyncTriggerStatus;
    mailbox_id: number;
    scope: string;
    sync_job_id: string;
    accepted_at: string;
}

export interface MailboxStatusItem {
    id: number;
    provider: string;
    email_address: string;
    is_primary: boolean;
    connection_state: MailboxConnectionState | string;
    sync_health: MailboxSyncHealth | string;
    sync_in_progress: boolean;
    last_successful_sync?: string | null;
    error_class?: MailboxErrorClass | string | null;
    sync_error?: string | null;
    sync_started_at?: string | null;
    email_count?: number;
    /**
     * False for provider-backed mailboxes — POST /mailboxes/{id}/sync rejects
     * Gmail-syntax custom queries for them, so the UI must not offer the field.
     */
    supports_custom_query?: boolean;
    /** When set (0–7), \"Open in Gmail\" uses mail.google.com/mail/u/N/ for this mailbox. */
    gmail_web_authuser_index?: number | null;
    created_at?: string | null;
    active_sync_job?: SyncJob | null;
    last_sync_job?: SyncJob | null;
    /** @deprecated Prefer connection_state */
    status: string;
    /** @deprecated Prefer sync_health + sync_in_progress */
    sync_status?: string | null;
    /** @deprecated Prefer last_successful_sync */
    last_synced_at?: string | null;
    /**
     * Set when the person or a plan limit disconnected this mailbox.
     * Null when provider health revoked the grant.
     */
    revoked_at?: string | null;
}

export const TERMINAL_SYNC_STAGES: ReadonlySet<SyncStage> = new Set<SyncStage>([
    'complete',
    'failed',
]);

export function isTerminalSyncStage(stage: SyncStage | string | undefined | null): boolean {
    return stage != null && TERMINAL_SYNC_STAGES.has(stage as SyncStage);
}
